//src/app/hooks/useClientPendingActions.ts
import { useGetOrdersQuery } from 'app/services/payments/orders';
import { useGetEngagementLetterStatsQuery } from 'app/services/engagementLetters';
// TODO: add the questionnaires stats hook when it is ready
import { useMemo } from 'react';

/**
 * Gather every “pending” thing a client might need to do.
 * The hook is a NO‑OP when `enabled` is false (e.g. user is not a client).
 */
export function useClientPendingActions(enabled: boolean) {
  /* ---- Orders that still need to be paid -------------------------------- */
  const { data: orders, isFetching: ordersLoading } = useGetOrdersQuery(
    { filters: { state: 'pending_payment' }, per_page: 1 },
    { skip: !enabled },
  );

  /* ---- Engagement letters not yet signed -------------------------------- */
  const { data: lettersStats, isFetching: lettersLoading } =
    useGetEngagementLetterStatsQuery({ filters: {} }, { skip: !enabled });

  // Same idea for questionnaires (draft / not submitted) when the endpoint exists
  // const { data: qStats, isFetching: qLoading } = useGetQuestionnairesStatsQuery(...);

  return useMemo(() => {
    if (!enabled) return { total: 0, first: null as null, loading: false };

    type Item = {
      key: 'orders' | 'letters' | 'questionnaires';
      count: number;
      href: string;
      label: string;
    };

    const items: Item[] = [];

    /* ---------- Orders --------------------------------------------------- */
    // Some back‑ends put the total in meta.total_count, some in totalCount,
    // so we probe both and fall back to the page length.
    const pendingOrders =
      (orders as any)?.meta?.total_count ??
      (orders as any)?.totalCount ??
      (orders as any)?.total_count ??
      (orders as any)?.data?.length ??
      0;

    if (pendingOrders > 0) {
      items.push({
        key: 'orders',
        count: pendingOrders,
        href: '/dashboard/orders',
        label: 'My Payments',
      });
    }

    /* ---------- Engagement letters -------------------------------------- */
    const unsignedLetters =
      lettersStats?.engagement_letters_unsigned_count ?? 0;

    if (unsignedLetters > 0) {
      items.push({
        key: 'letters',
        count: unsignedLetters,
        href: '/dashboard/engagementLetters',
        label: 'unsigned engagement letter',
      });
    }

    /* ---------- Questionnaires (when ready) ----------------------------- */
    // const draftQs = qStats?.questionnaires_draft_count ?? 0;
    // if (draftQs > 0) items.push({ key:'questionnaires', ... });

    return {
      total: items.reduce((sum, i) => sum + i.count, 0),
      first: items[0] ?? null,
      loading: ordersLoading || lettersLoading /* || qLoading */,
    };
  }, [enabled, orders, lettersStats, ordersLoading, lettersLoading]);
}
